import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  useApi,
  type ClosedWhanauListResponse,
  type WhanauListResponse,
} from "../api/client";
import "./DirectoryPicker.css";

/**
 * Whānau directory picker at /directory.
 *
 * Two-tile landing that lets the kaimahi drill into either the active
 * (open) list or the closed list. Each tile shows a live count fetched
 * in parallel on mount. If a count fails to load the tile still works,
 * it just doesn't show the badge — the underlying page will surface the
 * real error.
 *
 * Sub-routes:
 *   /directory/open    — DirectoryView (the active list)
 *   /directory/closed  — ClosedDirectoryView
 */
export function DirectoryPicker() {
  const { t } = useTranslation();
  const callApi = useApi();

  const [openCount, setOpenCount] = useState<number | null>(null);
  const [closedCount, setClosedCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Fire both in parallel. Failures are silently swallowed — the count
    // badge is a nice-to-have, not load-bearing.
    void (async () => {
      try {
        const data = await callApi<WhanauListResponse>("/whanau");
        if (!cancelled) setOpenCount(data.count);
      } catch {
        /* leave openCount null */
      }
    })();
    void (async () => {
      try {
        const data = await callApi<ClosedWhanauListResponse>("/whanau/closed");
        if (!cancelled) setClosedCount(data.count);
      } catch {
        /* leave closedCount null */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [callApi]);

  return (
    <section
      className="directory-picker"
      aria-labelledby="directory-picker-title"
    >
      <header className="directory-picker__header">
        <h1 id="directory-picker-title" className="directory-picker__title">
          {t("directoryPicker.title")}
        </h1>
        <p className="directory-picker__subtitle">
          {t("directoryPicker.subtitle")}
        </p>
      </header>

      <div className="directory-picker__tiles">
        <Link
          to="/directory/open"
          className="directory-picker__tile directory-picker__tile--open"
        >
          <span className="directory-picker__tile-label">
            {t("directoryPicker.openLabel")}
          </span>
          <span className="directory-picker__tile-count">
            {openCount !== null
              ? t("directoryPicker.count", { count: openCount })
              : t("directoryPicker.loadingCount")}
          </span>
          <span className="directory-picker__tile-hint">
            {t("directoryPicker.openHint")}
          </span>
        </Link>

        <Link
          to="/directory/closed"
          className="directory-picker__tile directory-picker__tile--closed"
        >
          <span className="directory-picker__tile-label">
            {t("directoryPicker.closedLabel")}
          </span>
          <span className="directory-picker__tile-count">
            {closedCount !== null
              ? t("directoryPicker.count", { count: closedCount })
              : t("directoryPicker.loadingCount")}
          </span>
          <span className="directory-picker__tile-hint">
            {t("directoryPicker.closedHint")}
          </span>
        </Link>
      </div>
    </section>
  );
}

export default DirectoryPicker;
